import { useGameStore, useSessionStore } from '@/app/stores';
import { attachDriverTap, updateAnalyticsMeta } from '@/shared/lib/analytics';
import { DriverStatusKind, type GameDriver } from '@/shared/lib/driver';

let currentUnsubMessage: (() => void) | null = null;
let currentUnsubStatus: (() => void) | null = null;
let currentTapUnsub: (() => void) | null = null;
let currentTapSetSelfId: ((id: string | null) => void) | null = null;
let currentDriver: GameDriver | null = null;

/**
 * Wires the active `GameDriver` (sessionStore) into the gameStore reducer
 * and analytics tap. Identity flips re-bind the analytics self-id and are
 * re-read at message-receive time so a late identity hydration doesn't
 * leave the closure with stale `null`.
 */
export const wireDriverSubscriptions = (): void => {
  useSessionStore.subscribe(
    (s) => s.driver,
    (driver) => {
      currentUnsubMessage?.();
      currentUnsubStatus?.();
      currentTapUnsub?.();
      currentUnsubMessage = null;
      currentUnsubStatus = null;
      currentTapUnsub = null;
      currentTapSetSelfId = null;
      currentDriver?.stop();
      currentDriver = driver;

      if (!driver) {
        useSessionStore.getState().setDriverStatus({ kind: DriverStatusKind.Idle });
        updateAnalyticsMeta({ userData: { mode: null } });
        return;
      }

      currentUnsubMessage = driver.onMessage((msg) =>
        useGameStore.getState().applyServerMsg(msg, {
          selfPlayerId: useSessionStore.getState().identity?.playerId ?? null,
        }),
      );
      currentUnsubStatus = driver.onStatus((status) =>
        useSessionStore.getState().setDriverStatus(status),
      );
      const tap = attachDriverTap(driver, useSessionStore.getState().identity?.playerId ?? null);
      currentTapUnsub = tap.unsubscribe;
      currentTapSetSelfId = tap.setSelfId;
      updateAnalyticsMeta({ userData: { mode: driver.mode } });
      void driver.start();
    },
    { fireImmediately: true },
  );

  useSessionStore.subscribe(
    (s) => s.identity,
    (identity) => {
      currentTapSetSelfId?.(identity?.playerId ?? null);
      if (identity) {
        updateAnalyticsMeta({
          userId: identity.playerId,
          userData: { identitySource: identity.source },
        });
      } else {
        updateAnalyticsMeta({ userId: null, userData: { identitySource: null } });
      }
    },
    { fireImmediately: true },
  );
};
